import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthCard } from './_AuthLayout';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/signin'), 2000);
    } catch (err: any) {
      setError(err.message || 'Reset failed — request a new link');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthCard title="Invalid link" subtitle="This reset link is missing or expired.">
        <Link to="/forgot-password" className="block w-full">
          <Button size="lg" className="w-full">Request a new link</Button>
        </Link>
        <div className="mt-4 text-center">
          <Link to="/signin" className="text-xs text-fg-muted hover:text-accent">← Back to sign in</Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set a new password" subtitle="Choose a strong password to finish resetting your account.">
      {done ? (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-accent/10 border border-accent/30 text-accent text-sm" role="status">
            Password updated. Redirecting to sign in…
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="relative">
            <Input
              label="New password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              autoComplete="new-password"
              minLength={8}
              hint="Minimum 8 characters."
              className="pr-10"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              tabIndex={-1}
              className={cn(
                'absolute right-2 top-[26px] inline-flex items-center justify-center size-9 rounded-md',
                'text-fg-subtle hover:text-fg transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
              )}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          <Input
            label="Confirm password"
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Re-enter the same password"
            required
            autoComplete="new-password"
          />
          {error && (
            <div role="alert" className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <Button type="submit" size="lg" loading={loading} className="w-full">Set new password</Button>
        </form>
      )}
    </AuthCard>
  );
}
